import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from './env';
import { STORAGE_KEYS } from './constants';
import type { ApiError, AuthTokens } from '@/types/api';

const BASE_URL = `${env.apiBaseUrl}/api/v1`;

/* ----------------------------------------------------------------------------
 * Token storage — pure helpers, no React.
 * --------------------------------------------------------------------------*/

function readTokens(key: string): AuthTokens | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthTokens>;
    if (typeof parsed.access !== 'string' || typeof parsed.refresh !== 'string') {
      return null;
    }
    return { access: parsed.access, refresh: parsed.refresh };
  } catch {
    return null;
  }
}

function writeTokens(key: string, tokens: AuthTokens | null) {
  if (!tokens) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify(tokens));
}

export const tokenStore = {
  getUser: () => readTokens(STORAGE_KEYS.authTokens),
  setUser: (tokens: AuthTokens | null) => writeTokens(STORAGE_KEYS.authTokens, tokens),
  getAdmin: () => readTokens(STORAGE_KEYS.adminAuthTokens),
  setAdmin: (tokens: AuthTokens | null) => writeTokens(STORAGE_KEYS.adminAuthTokens, tokens),
};

/* ----------------------------------------------------------------------------
 * Axios factory — produces both `api` (user JWT) and `adminApi` (admin JWT)
 * with refresh-and-retry on 401.
 * --------------------------------------------------------------------------*/

type AuthFlavor = 'user' | 'admin';

interface RetriedRequest extends InternalAxiosRequestConfig {
  _retried?: boolean;
  _skipAuth?: boolean;
}

function makeClient(flavor: AuthFlavor): AxiosInstance {
  const client = axios.create({
    baseURL: BASE_URL,
    timeout: 30_000,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

  const get = flavor === 'admin' ? tokenStore.getAdmin : tokenStore.getUser;
  const set = flavor === 'admin' ? tokenStore.setAdmin : tokenStore.setUser;

  client.interceptors.request.use((config) => {
    const req = config as RetriedRequest;
    if (req._skipAuth) return config;
    const tokens = get();
    if (tokens?.access) {
      config.headers.set('Authorization', `Bearer ${tokens.access}`);
    }
    return config;
  });

  // Single-flight refresh: queue concurrent 401s onto one refresh promise.
  let refreshing: Promise<string | null> | null = null;

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetriedRequest | undefined;
      const status = error.response?.status;

      if (
        !config ||
        config._retried ||
        config._skipAuth ||
        status !== 401 ||
        config.url?.includes('/auth/token/refresh/') ||
        config.url?.includes('/auth/login/')
      ) {
        return Promise.reject(normalizeError(error));
      }

      const tokens = get();
      if (!tokens?.refresh) {
        set(null);
        broadcastSignOut(flavor);
        return Promise.reject(normalizeError(error));
      }

      refreshing ??= refreshAccessToken(tokens.refresh)
        .then((next) => {
          if (next) {
            set(next);
            return next.access;
          }
          set(null);
          broadcastSignOut(flavor);
          return null;
        })
        .finally(() => {
          refreshing = null;
        });

      const newAccess = await refreshing;
      if (!newAccess) {
        return Promise.reject(normalizeError(error));
      }

      config._retried = true;
      config.headers.set('Authorization', `Bearer ${newAccess}`);
      return client.request(config);
    },
  );

  return client;
}

async function refreshAccessToken(refresh: string): Promise<AuthTokens | null> {
  try {
    const response = await axios.post<{ access: string; refresh?: string }>(
      `${BASE_URL}/auth/token/refresh/`,
      { refresh },
      { headers: { 'Content-Type': 'application/json' }, timeout: 10_000 },
    );
    return {
      access: response.data.access,
      refresh: response.data.refresh ?? refresh,
    };
  } catch {
    return null;
  }
}

const SIGN_OUT_EVENT = 'tally:auth:signed-out';
function broadcastSignOut(flavor: AuthFlavor) {
  window.dispatchEvent(new CustomEvent(SIGN_OUT_EVENT, { detail: { flavor } }));
}

export function onSignOut(handler: (flavor: AuthFlavor) => void): () => void {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<{ flavor: AuthFlavor }>).detail;
    handler(detail.flavor);
  };
  window.addEventListener(SIGN_OUT_EVENT, listener);
  return () => window.removeEventListener(SIGN_OUT_EVENT, listener);
}

export function normalizeError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const response = error.response;
    const status = response?.status ?? 0;
    const data = response?.data as Record<string, unknown> | undefined;
    const fieldErrors: Record<string, string[]> = {};

    if (data && typeof data === 'object') {
      for (const [key, value] of Object.entries(data)) {
        if (Array.isArray(value)) {
          fieldErrors[key] = value.map(String);
        }
      }
    }

    const detail =
      (data?.detail as string | undefined) ??
      (data?.message as string | undefined) ??
      (data?.error as string | undefined);

    return {
      status,
      code: (data?.code as string | undefined) ?? (data?.error as string | undefined),
      detail,
      message:
        detail ??
        (status === 0
          ? 'Network error. Check your connection and try again.'
          : status >= 500
            ? 'Something went wrong on our side. Please try again shortly.'
            : 'Request failed. Please review and try again.'),
      fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : undefined,
    };
  }
  return {
    status: 0,
    message: error instanceof Error ? error.message : 'Unknown error',
  };
}

export const api = makeClient('user');
export const adminApi = makeClient('admin');

/** For requests that must not include any Authorization header. */
export async function publicRequest<T>(
  url: string,
  config: AxiosRequestConfig = {},
): Promise<T> {
  const merged: AxiosRequestConfig = { ...config, baseURL: BASE_URL };
  const req = merged as InternalAxiosRequestConfig & { _skipAuth?: boolean };
  req._skipAuth = true;
  const { data } = await axios.request<T>({
    ...merged,
    url,
    headers: { ...(merged.headers as Record<string, string> | undefined), 'Content-Type': 'application/json' },
    timeout: merged.timeout ?? 30_000,
  });
  return data;
}
